import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ShareFolder from "./ShareFolder";
import InfoFolder from "./InfoFolder";
import PopUpMentionLegales from "./PopUpMentionLegales";
import "../css/Home.css";
import { logAction } from "./log";
import Split from 'react-split'
// import { Iconoir } from 'iconoir-react';

/**
 * Composant Home gérant la vue principale après connexion,
 * avec accès aux dossiers partagés, affichage des infos,
 * gestion de la déconnexion, et popup mentions légales.
 */
const Home = () => {
    // État pour savoir si la vérification d'authentification est en cours
    const [isAuthLoading, setIsAuthLoading] = useState(true);
    // Arbre des dossiers/fichiers partagés récupéré depuis l'API
    const [sharedTree, setSharedTree] = useState([]);
    // Nom du dossier de base (stocké en localStorage pour persistance)
    const [baseFolder, setBaseFolder] = useState(localStorage.getItem('basefolder') || "");
    // Email de l'utilisateur connecté
    const [userEmail, setUserEmail] = useState(null);
    // Droit d'accès / autorisation de partage de l'utilisateur
    const [authorisation, setAuthorisation] = useState(false);
    // Chemin courant sous forme de tableau (ex: ["dossier1", "sousDossier"])
    const [currentPath, setCurrentPath] = useState([]);
    // Chemin courant sous forme de chaîne avec séparateur backslash (ex: "dossier1\sousDossier")
    const [currentPathString, setCurrentPathString] = useState("");
    // Chemin racine (repertoire initial)
    const [rootPath, setRootPath] = useState("");
    // Nom de l'office, persisté en localStorage
    const [officeName, setOfficeName] = useState(localStorage.getItem('officeName') || "");
    // État d'affichage du popup des mentions légales
    const [isLegalPopupOpen, setIsLegalPopupOpen] = useState(false);
    
    const[to,setTo] = useState(null);

    // Référence mutable pour stocker l'id du dossier sans déclencher de rerender
    const folderIdRef = useRef(null);
    // Référence pour indiquer si l'accès au dossier partagé a déjà été récupéré (évite doublons)
    const hasFetched = useRef(false);
    // URL de base de l'API, injectée depuis variables d'environnement
    const API_URL = process.env.REACT_APP_API_URL;
    // Hook de navigation React Router pour changer de page
    const navigate = useNavigate();

    // Logo (url ou texte) affiché en haut à gauche
    const [logo, setLogo] = useState(null);

    /**
     * Transforme une chaîne de chemin en tableau de dossiers
     * ex: "dossier1\sousDossier" => ["dossier1", "sousDossier"]
     * @param {string} path
     * @returns {string[]}
     */
    const updateCurrentPath = (path) => path.split("\\");

    /**
     * Récupère la valeur d'un cookie par son nom
     * @param {string} name - nom du cookie à récupérer
     * @returns {string|null} valeur du cookie ou null si absent
     */
    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        return parts.length === 2 ? parts.pop().split(";").shift() : null;
    };
    document.getCookie = getCookie;
    /**
     * Teste si une chaîne est une URL HTTP(S)
     * @param {string} str
     * @returns {boolean}
     */
    const isUrl = (str) => typeof str === "string" && /^https?:\/\//i.test(str);

    /**
     * Fonction de déconnexion :
     * - Log l'action de déconnexion
     * - Supprime tous les cookies sauf "remember"
     * - Vide le localStorage
     * - Restaure le cookie remember si présent pour persistance
     * - Redirige vers la page de login "/"
     */
    const handleLogout = () => {
        const rememberCookie = getCookie("remember");
        const userId = getCookie("authEmail");
        const dossierId = localStorage.getItem('dossierId');

        logAction({
            action: "disconnection",
            dossierId,
            utilisateurId: userId,
        });

        // Suppression de tous les cookies sauf "remember"
        document.cookie.split(";").forEach((cookie) => {
            const eqPos = cookie.indexOf("=");
            const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
            if (name !== "remember") {
                document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
            }
        });

        // Nettoyage du localStorage
        localStorage.clear();

        // Remise du cookie remember pour persistance (10 ans)
        if (rememberCookie) {
            document.cookie = `remember=${rememberCookie}; path=/; max-age=${60 * 60 * 24 * 365 * 10}; Secure; SameSite=Strict`;
        }

        navigate("/");
    };

    /**
     * Récupère le logo stocké dans un cookie "userLogo" au chargement
     * et le décode si nécessaire
     */
    useEffect(() => {

        const logoFromCookie = getCookie("userLogo");
        if (logoFromCookie) {
            let decodedLogo;
            try {
                decodedLogo = decodeURIComponent(logoFromCookie);
            } catch {
                decodedLogo = logoFromCookie;
            }
            setLogo(decodedLogo);
        }
    }, []);

    /**
     * Charge le contenu d'un dossier partagé depuis l'API
     * @param {string} email - email utilisateur (actuellement non utilisé dans la requête)
     * @param {string} path - chemin complet du dossier à charger
     */
    const loadFolder = useCallback(async (email, path) => {
        const newPathArray = updateCurrentPath(path);

        try {
            const res = await fetch(`${API_URL}/api/getShareFolders.php`, {
                method: "POST",
                credentials: 'include',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ dossier_id: folderIdRef.current, repertoire: path }),
            });

            const { tree } = await res.json();

            if (Array.isArray(tree)) {
                setSharedTree(tree);
            } else {
                setSharedTree([]);
            }

            setCurrentPath(newPathArray);
        } catch (err) {
            console.error("Erreur lors du chargement du dossier :", err);
        }
    }, []);

    /**
     * Navigue vers un chemin donné sous forme de tableau
     * (met à jour le chemin courant et recharge le dossier)
     * @param {string[]} pathArray
     */
    const navigateToPath = useCallback(async (pathArray) => {
      if (pathArray == null) return;
      let pathString = "";
      if (Array.isArray(pathArray)) {
        pathString = pathArray.join("\\");
        if (pathString !== currentPathString) {
            setCurrentPath(pathArray);
            setCurrentPathString(pathString);
        }
      }else{
        pathString = pathArray;
      }
        await loadFolder(userEmail, pathString);
    }, [currentPathString, loadFolder, userEmail, rootPath]);

    /**
     * Vérifie l'authentification au chargement du composant
     * Si non authentifié, redirige vers la page d'accueil
     * Sinon initialise email et autorisations.
     */
    useEffect(() => {
        fetch(`${API_URL}/controllers/isAuthenticated.php`, {
            credentials: "include",
        })
            .then(res => res.json())
            .then(data => {
                setIsAuthLoading(false);
                if (!data.authenticated) {
                    navigate("/");
                    return;
                }
                setUserEmail(data.email);
                setTo(data.to);
                // setAuthorisation(data.partage);
                setAuthorisation(2);
            })
            .catch(() => {
                setIsAuthLoading(false);
                navigate("/");
            });




            
    }, [navigate]);

    /**
     * Récupère l'accès au dossier partagé après
     * que l'email utilisateur soit défini.
     * Charge la baseFolder, dossier_id, et l'arbre des dossiers.
     * Ne s'exécute qu'une fois grâce au flag hasFetched.
     */
    useEffect(() => {
        const fetchAccess = async () => {
            if (!userEmail || hasFetched.current) return;

            try {
                const res = await fetch(`${API_URL}/controllers/checkFolderSharedAccess.php`, {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email: userEmail, key : localStorage.getItem('key') }),
                });

                const access = await res.json();
                if (access.success) {
                    console.log("Accès au dossier partagé obtenu :", access);

                    setBaseFolder(access.dossier_nom);
                    folderIdRef.current = access.dossier_id;
                    setRootPath(access.repertoire || "");
                    await loadFolder(userEmail, access.repertoire);
                } else {
                    setBaseFolder(null);
                    setSharedTree([]);
                }

                hasFetched.current = true;
            } catch (err) {
                console.error("Erreur lors de la vérification d'accès :", err);
            }
        };



      const fetchParameters = async () => {

        try {
          const res = await fetch(`${API_URL}/api/getParameters.php`, {
            method: "GET", // or POST, PUT, DELETE, etc
            credentials: "include",
            // headers: {
            Authorization: `Bearer ${to}`, // Add the token here
            "Content-Type": "application/json" // Optional: Specify content type
            // }
          });

          const params= await res.json();

          if (params) {
              console.log("Paramètres récupérés :", params);
              document.cookie = 'legal=' + params.mentionslegales + '; path=/; Secure; SameSite=Strict';
          } else {
              console.error("Aucun paramètre récupéré.");
          }

        } catch (err) {
          console.error("Erreur lors du chargement du dossier :", err);
        }
      };

      fetchAccess();
    //   fetchParameters();

    }, [userEmail, loadFolder]);

    /**
     * Met à jour le titre de la page selon le nom de l'office et dossier de base
     */
    useEffect(() => {
        if (officeName && baseFolder) {
            document.title = `${officeName} - ${baseFolder}`;
        } else {
            document.title = "Partage de dossiers";
        }
    }, [officeName, baseFolder]);

    // Affiche un message pendant le chargement de l'authentification
    if (isAuthLoading) {
        return <p>Vérification de l'authentification...</p>;
    }
  

    return (
      <div className="home">
        <div className="content">
          <Split className="split" sizes={[25, 75]} minSize={250} gutterSize={6}>
            <div className="left-panel">
              <div className="logo-container m-0 sm:mb-4" onClick={() => navigateToPath(rootPath)} style={{ cursor: 'pointer' }}>
                  {logo && isUrl(logo) ? (
                      <img src={logo} alt="Logo" className="logo-image"/>
                  ) : logo ? (
                      <span className="navbar-logo-text">{logo}</span>
                  ) : null}
              </div>

              {/* <div class="breadcrumbs text-sm">
                <ul>
                  <li> Arborescence</li>
                </ul>
              </div> */}
              <ShareFolder
                  dossier_id={folderIdRef.current}
                  baseFolder={baseFolder}
                  sharedTree={sharedTree}
                  currentPath={currentPath}
                  navigateToPath={navigateToPath}
              />
            </div>
            {/* <Iconoir color="red" height={36} width={36} /> */}
            <div className="right-panel">
              <InfoFolder
                  dossier_id={folderIdRef.current}
                  tree={sharedTree}
                  baseFolder={baseFolder}
                  currentPath={currentPath}
                  navigateToPath={navigateToPath}
                  userEmail={userEmail}
                  authorisation={authorisation}
                  navigate={navigate}
                  setIsLegalPopupOpen={setIsLegalPopupOpen}
                  handleLogout={handleLogout}
              />
            </div>
          </Split>
            <PopUpMentionLegales
                isOpen={isLegalPopupOpen}
                onClose={() => setIsLegalPopupOpen(false)}
            />

        </div>
      </div>
    );
};

export default Home;
